import React from 'react';
import { spacing } from '../../theme/spacing';
import { typography } from '../../theme/typography';
import { colors } from '../../theme/colors';

// --- Main Step5 Component ---

interface Step5Props {
    data: any; // Using loose type or WizardState
    error: string | null;
    onNavigateToStep?: (step: number) => void;
}

export const Step5: React.FC<Step5Props> = ({ data, error, onNavigateToStep }) => {

    // Helper to format text
    const txt = (val?: string) => val || '-';

    // Mappers
    const getTimeLabel = (t?: string, week?: string) => {
        if (!t) return '-';
        if (t === '7days') return 'Seuraavan 7 päivän aikana';
        if (t === '30days') return 'Seuraavan 30 päivän aikana';
        if (t === 'specific') return week ? `Viikko ${week}` : 'Määritelty viikko';
        return t;
    };

    const getDeliveryLabel = (d?: string) => {
        if (!d) return '-';
        if (d === 'water') return 'Tuon veneen vesitse';
        if (d === 'trailer') return 'Tuon veneen trailerilla';
        if (d === 'pickup') return 'Noutopalvelu';
        return d;
    };

    const titleStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '28px',
        fontWeight: typography.weights.bold,
        color: colors.text.primary,
        marginBottom: spacing[12],
        marginTop: 0,
    };

    const helperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        color: colors.text.muted,
        marginBottom: spacing[48],
        lineHeight: 1.6,
    };

    const sectionHeaderStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        fontWeight: typography.weights.semiBold,
        color: colors.text.primary,
        marginBottom: spacing[16],
        marginTop: 0,
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
    };

    const editLinkStyle: React.CSSProperties = {
        fontSize: '14px',
        fontWeight: typography.weights.medium,
        color: colors.primary.navy,
        cursor: 'pointer',
        textDecoration: 'underline',
    };

    const dividerStyle: React.CSSProperties = {
        borderTop: '1px solid #E5E7EB',
        margin: `${spacing[24]} 0`,
    };

    const rowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: spacing[8],
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        lineHeight: 1.5,
    };

    const labelStyle: React.CSSProperties = {
        color: colors.text.muted,
    };

    const valueStyle: React.CSSProperties = {
        color: colors.text.primary,
        fontWeight: typography.weights.medium,
        textAlign: 'right' as const,
        maxWidth: '60%',
    };

    const priceRowStyle: React.CSSProperties = {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        marginBottom: spacing[8],
        fontFamily: typography.fontFamily,
    };

    const priceLabelStyle: React.CSSProperties = {
        fontSize: '16px',
        fontWeight: typography.weights.semiBold,
        color: colors.text.primary,
    };

    const priceValueStyle: React.CSSProperties = {
        fontSize: '24px',
        fontWeight: typography.weights.bold,
        color: colors.text.primary,
    };

    const priceHelperStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.small.fontSize,
        color: colors.text.muted,
        lineHeight: 1.5,
        marginTop: spacing[4],
    };

    const nextStepsHeaderStyle: React.CSSProperties = {
        fontFamily: typography.fontFamily,
        fontSize: '16px',
        fontWeight: typography.weights.semiBold,
        color: colors.text.primary,
        marginTop: spacing[32],
        marginBottom: spacing[12],
    };

    const nextStepsListStyle: React.CSSProperties = {
        margin: 0,
        paddingLeft: spacing[24],
        fontFamily: typography.fontFamily,
        fontSize: typography.sizes.body.fontSize,
        color: colors.text.muted,
        lineHeight: 1.8,
    };

    return (
        <div>
            {/* Header */}
            <h2 style={titleStyle}>Yhteenveto varauspyynnöstä</h2>
            <p style={helperStyle}>
                Tarkista tiedot rauhassa. Tämä on varauspyyntö – vahvistamme tarkan huoltopäivän sähköpostitse 1–2 arkipäivässä.
            </p>

            {/* Vene ja moottori */}
            <h3 style={sectionHeaderStyle}>
                Vene ja moottori
                {onNavigateToStep && <span style={editLinkStyle} onClick={() => onNavigateToStep(1)}>Muokkaa</span>}
            </h3>
            <div style={rowStyle}>
                <span style={labelStyle}>Vene</span>
                <span style={valueStyle}>{txt(data.boat?.brandName)} {txt(data.boat?.modelName)}</span>
            </div>
            <div style={rowStyle}>
                <span style={labelStyle}>Moottori</span>
                <span style={valueStyle}>{txt(data.engine?.brandName)} {txt(data.engine?.modelName)} {data.engine?.year ? `(${data.engine.year})` : ''}</span>
            </div>
            {data.boat?.registration && (
                <div style={rowStyle}>
                    <span style={labelStyle}>Rekisterinumero</span>
                    <span style={valueStyle}>{data.boat.registration}</span>
                </div>
            )}

            <div style={dividerStyle} />

            {/* Valittu huolto */}
            <h3 style={sectionHeaderStyle}>
                Valittu huolto
                {onNavigateToStep && <span style={editLinkStyle} onClick={() => onNavigateToStep(2)}>Muokkaa</span>}
            </h3>
            <div style={rowStyle}>
                <span style={labelStyle}>Huoltopaketti</span>
                <span style={valueStyle}>{txt(data.service?.packageTitle)}</span>
            </div>

            <div style={dividerStyle} />

            {/* Ajankohta ja toimitus */}
            <h3 style={sectionHeaderStyle}>
                Ajankohta ja toimitus
                {onNavigateToStep && <span style={editLinkStyle} onClick={() => onNavigateToStep(3)}>Muokkaa</span>}
            </h3>
            <div style={rowStyle}>
                <span style={labelStyle}>Toiveajankohta</span>
                <span style={valueStyle}>{getTimeLabel(data.timeAndDelivery?.timePreference, data.timeAndDelivery?.specificWeek)}</span>
            </div>
            <div style={rowStyle}>
                <span style={labelStyle}>Toimitustapa</span>
                <span style={valueStyle}>{getDeliveryLabel(data.timeAndDelivery?.deliveryMethod)}</span>
            </div>
            {data.timeAndDelivery?.deliveryMethod === 'pickup' && data.timeAndDelivery?.pickupAddress && (
                <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                    <span style={labelStyle}>Nouto-osoite</span>
                    <span style={{ ...valueStyle, textAlign: 'left', maxWidth: '100%' }}>
                        {txt(data.timeAndDelivery.pickupAddress.street)}, {txt(data.timeAndDelivery.pickupAddress.city)}
                    </span>
                </div>
            )}

            <div style={dividerStyle} />

            {/* Yhteystiedot */}
            <h3 style={sectionHeaderStyle}>
                Yhteystiedot
                {onNavigateToStep && <span style={editLinkStyle} onClick={() => onNavigateToStep(4)}>Muokkaa</span>}
            </h3>
            <div style={rowStyle}>
                <span style={labelStyle}>Nimi</span>
                <span style={valueStyle}>{txt(data.contact?.firstName)} {txt(data.contact?.lastName)}</span>
            </div>
            <div style={rowStyle}>
                <span style={labelStyle}>Puhelin</span>
                <span style={valueStyle}>{txt(data.contact?.phone)}</span>
            </div>
            <div style={rowStyle}>
                <span style={labelStyle}>Sähköposti</span>
                <span style={valueStyle}>{txt(data.contact?.email)}</span>
            </div>
            {data.contact?.notes && (
                <div style={{ ...rowStyle, flexDirection: 'column', alignItems: 'flex-start', gap: '4px' }}>
                    <span style={labelStyle}>Lisätiedot</span>
                    <span style={{ ...valueStyle, textAlign: 'left', maxWidth: '100%', whiteSpace: 'pre-wrap' }}>
                        {data.contact.notes}
                    </span>
                </div>
            )}

            <div style={dividerStyle} />

            {/* Price */}
            <div style={priceRowStyle}>
                <span style={priceLabelStyle}>Hinta-arvio</span>
                <span style={priceValueStyle}>{data.service?.packagePrice || '-'}</span>
            </div>
            <div style={priceHelperStyle}>
                {data.service?.selectedPackageId === 'other'
                    ? 'Hinta tarkentuu kun olemme käsitelleet pyynnön.'
                    : 'Hinta sisältää työn ja tarvikkeet. Mahdolliset lisätyöt sovitaan erikseen.'}
            </div>

            {/* What happens next */}
            <h4 style={nextStepsHeaderStyle}>Mitä tapahtuu seuraavaksi?</h4>
            <ol style={nextStepsListStyle}>
                <li>Käymme varauspyyntösi läpi.</li>
                <li>Vahvistamme tarkan huoltopäivän 1–2 arkipäivässä.</li>
                <li>Saat vahvistuksen sähköpostiisi.</li>
            </ol>
        </div>
    );
};



